import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function TerminationForConvenience() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            Plain-English Explanation of Termination for Convenience Clause
          </h1>

          <p className="text-base md:text-lg leading-relaxed mb-12" style={{ color: colors.textMuted }}>
            A termination for convenience clause allows one party to end the contract without proving breach or fault, usually with advance notice.
          </p>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              This clause provides an exit option when circumstances change or the relationship isn't working, even if no one has done anything wrong. It typically requires advance notice and may involve payment for work completed or compensation for early termination.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Why This Clause Exists</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Businesses include convenience termination clauses to maintain flexibility when priorities change, budgets shift, or strategies evolve. It avoids forcing parties to continue unprofitable or unproductive relationships. For service providers, it ensures fair compensation when clients exit early.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
            <ul className="space-y-3 ml-6">
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Either party can exit without proving wrongdoing
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                May require 30-90 days notice or equivalent payment
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                You must be paid for completed work
              </li>
              <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Some contracts include early termination fees
              </li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>What to Watch Out For</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Check notice requirements, whether termination fees apply, how partial work is compensated, and if the clause is mutual or one-sided. Client-side-only termination clauses give them flexibility while locking you in. Ensure fair compensation for disrupted plans or lost opportunities.
            </p>
          </section>

          <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
            <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
              Termination for convenience doesn't eliminate other obligations like confidentiality or IP assignment. Completed deliverables typically remain the client's property. This explanation provides general information but doesn't constitute legal advice about your specific situation.
            </p>
          </section>

          <section className="mb-12">
            <div className="flex gap-4 mb-8">
              <Link to="/clauses" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                ← All Clauses
              </Link>
              <Link to="/clauses/business" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
                Business Clauses
              </Link>
            </div>
            <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/explain-payment-terms-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Payment Terms</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Understand payment schedules</p>
              </Link>
              <Link to="/explain-scope-of-work-clause" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Scope of Work</h3>
                <p className="text-sm" style={{ color: colors.textMuted }}>Learn about project boundaries</p>
              </Link>
            </div>
          </section>

          <div className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
            <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
            <Link to="/" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
              Try ClauseClear
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Sponsorship Placeholder */}
          <SponsorPlaceholder variant="full" />
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
